import * as THREE from 'three';
import { getBossScript } from '../sim/ai/brains';
import type { Actor } from '../sim/actor';
import type { ClassDef } from '../sim/registry';
import { chargesAvailable, canCast } from '../sim/skillRuntime';
import type { ResolvedSkill } from '../sim/skillTypes';
import { getStatus } from '../sim/status';
import { GRADE_RANK, SKILL_SLOTS } from '../sim/types';
import type { World } from '../sim/world';
import { DamageNumbers } from './DamageNumbers';
import { skillIcon, statusIcon } from './icons';
import { Minimap, type MinimapIcon } from './Minimap';
import { WorldLabels } from './WorldLabels';

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text !== undefined) el.textContent = text;
  parent?.appendChild(el);
  return el;
}

const LAYER_COLORS = ['#c8322b', '#d9772b', '#d8b233', '#7fb33a', '#2e9c8c', '#287782', '#1f3463', '#6b3fa0'];

export interface TrackerEntry {
  id: string;
  title: string;
  kind: 'main' | 'side' | 'daily' | 'weekly' | 'world';
  objectives: { text: string; progress?: string; done?: boolean }[];
}

export interface PartyMemberView {
  name: string;
  classLetter: string;
  hpPct: number;
  leader: boolean;
  dead: boolean;
  level: number;
}

export interface ItemSlotView {
  icon: string;
  count: number;
  cd: number;
  cdMax: number;
  name: string;
}

export interface HudState {
  player: Actor;
  world: World;
  cls: ClassDef;
  level: number;
  xpPct: number;
  legacyRank: number;
  zoneName: string;
  channel: string;
  currencies: { silver: number; gold: number; shards: number };
  tracker: TrackerEntry[];
  party: PartyMemberView[];
  items: (ItemSlotView | null)[];
  minimapIcons: MinimapIcon[];
  prompt: { key: string; text: string } | null;
}

interface SlotEls {
  root: HTMLDivElement;
  img: HTMLImageElement;
  cd: HTMLDivElement;
  cdn: HTMLDivElement;
  count: HTMLDivElement;
  skillId: string | null;
  lastCd: number;
  wasCooling: boolean;
}

/**
 * The in-game HUD. Built once, then updated every frame with change
 * detection so only values that moved touch the DOM.
 */
export class Hud {
  readonly root: HTMLDivElement;
  readonly worldLayer: HTMLDivElement;
  readonly damage: DamageNumbers;
  readonly labels: WorldLabels;
  readonly minimap = new Minimap();
  private hpBar!: HTMLDivElement;
  private hpFill!: HTMLElement;
  private hpLag!: HTMLElement;
  private hpShield!: HTMLElement;
  private hpText!: HTMLSpanElement;
  private mpFill!: HTMLElement;
  private mpText!: HTMLSpanElement;
  private slots = new Map<string, SlotEls>();
  private itemSlots: SlotEls[] = [];
  private crestRing!: SVGCircleElement;
  private crestLabel!: HTMLDivElement;
  private crestPips!: HTMLDivElement;
  private zBtn!: HTMLDivElement;
  private xBtn!: HTMLDivElement;
  private dashPip!: HTMLDivElement;
  private buffsL!: HTMLDivElement;
  private buffsR!: HTMLDivElement;
  private castbar!: HTMLDivElement;
  private castFill!: HTMLElement;
  private castZone!: HTMLElement;
  private castText!: HTMLSpanElement;
  private xpFill!: HTMLElement;
  private lvlText!: HTMLElement;
  private rosterText!: HTMLElement;
  private zoneText!: HTMLSpanElement;
  private chText!: HTMLSpanElement;
  private tracker!: HTMLDivElement;
  private trackerKey = '';
  private boss!: HTMLDivElement;
  private bossName!: HTMLSpanElement;
  private bossGrade!: HTMLSpanElement;
  private bossEmblem!: HTMLDivElement;
  private bossNext!: HTMLDivElement;
  private bossCur!: HTMLDivElement;
  private bossNum!: HTMLDivElement;
  private bossMult!: HTMLDivElement;
  private bossLvl!: HTMLDivElement;
  private bossStagger!: HTMLDivElement;
  private bossStaggerFill!: HTMLElement;
  private bossInfoL!: HTMLSpanElement;
  private bossInfoR!: HTMLSpanElement;
  private bossParts!: HTMLSpanElement;
  private party!: HTMLDivElement;
  private menuKeys: { el: HTMLElement; read: () => string }[] = [];
  /** Kind of the current zone (boss titles depend on the content type). */
  zoneKind = '';
  /** Clicking the quest tracker (opens the journal). */
  onTrackerClick: (() => void) | null = null;
  private partyKey = '';
  private toasts!: HTMLDivElement;
  private promptEl!: HTMLDivElement;
  private infoStrip!: HTMLDivElement;
  private buffKey = '';
  private lastHp = -1;
  private lastMp = -1;
  private lastShield = -1;
  private bossId = -1;
  private time = 0;
  private cluster!: HTMLDivElement;
  private awakeSlot!: SlotEls;
  private tmp = new THREE.Vector3();
  menuBar!: HTMLDivElement;

  constructor(readonly container: HTMLElement) {
    this.root = h('div', '', container);
    this.root.id = 'ui-root';
    this.worldLayer = h('div', '', this.root);
    this.worldLayer.style.cssText = 'position:absolute;inset:0;overflow:hidden';
    this.labels = new WorldLabels(this.worldLayer);
    const dmgLayer = h('div', '', this.root);
    dmgLayer.style.cssText = 'position:absolute;inset:0;overflow:hidden';
    this.damage = new DamageNumbers(dmgLayer);
    this.build();
  }

  private makeSlot(parent: HTMLElement, key: string, big = false): SlotEls {
    const root = h('div', `slot${big ? ' big' : ''}`, parent);
    const img = h('img', 'icon', root);
    img.alt = '';
    img.draggable = false;
    const cd = h('div', 'cd', root);
    const cdn = h('div', 'cdn', root);
    const count = h('div', 'count', root);
    h('div', 'key', root, key);
    return { root, img, cd, cdn, count, skillId: null, lastCd: -1, wasCooling: false };
  }

  private build(): void {
    const r = this.root;
    // Info strip.
    this.infoStrip = h('div', 'info-strip', r);

    // Minimap.
    const mm = h('div', 'minimap-wrap', r);
    const head = h('div', 'minimap-head', mm);
    this.zoneText = h('span', '', head, '');
    this.chText = h('span', 'ch', head, '');
    const map = h('div', 'minimap', mm);
    map.appendChild(this.minimap.canvas);
    this.tracker = h('div', 'tracker interactive', mm);
    this.tracker.title = 'Open the quest journal';
    this.tracker.addEventListener('click', () => this.onTrackerClick?.());

    // Boss bar.
    this.boss = h('div', 'bossbar hidden', r);
    const labels = h('div', 'labels', this.boss);
    this.bossGrade = h('span', 'grade', labels);
    this.bossName = h('span', 'name', labels);
    const row = h('div', 'row', this.boss);
    this.bossEmblem = h('div', 'emblem', row);
    h('span', '', this.bossEmblem, '✦');
    const body = h('div', 'body', row);
    const hp = h('div', 'hp', body);
    this.bossNext = h('div', 'next', hp);
    this.bossCur = h('div', 'cur', hp);
    this.bossLvl = h('div', 'lvl', hp);
    this.bossNum = h('div', 'num', hp);
    this.bossMult = h('div', 'mult', hp);
    this.bossStagger = h('div', 'stagger', body);
    this.bossStaggerFill = h('i', '', this.bossStagger);
    const info = h('div', 'info', body);
    this.bossInfoL = h('span', '', info);
    this.bossParts = h('span', 'parts', info);
    this.bossInfoR = h('span', 'enrage', info);

    // Party frames.
    this.party = h('div', 'party hidden', r);

    // Cast bar.
    this.castbar = h('div', 'castbar hidden', r);
    this.castFill = h('i', '', this.castbar);
    this.castZone = h('b', 'hidden', this.castbar);
    this.castText = h('span', '', this.castbar);

    // Combat cluster.
    this.cluster = h('div', 'hud-cluster', r);
    const left = h('div', 'hud-side', this.cluster);
    this.buffsL = h('div', 'buff-row left', left);
    this.hpBar = h('div', 'hud-bar hp left', left);
    this.hpLag = h('i', 'lag', this.hpBar);
    this.hpFill = h('i', '', this.hpBar);
    this.hpShield = h('i', 'shield', this.hpBar);
    this.hpShield.style.setProperty('--v', '0');
    this.hpText = h('span', '', this.hpBar);
    const skillArea = h('div', '', left);
    skillArea.style.cssText = 'display:flex;gap:0.6vh;align-items:flex-end';
    this.awakeSlot = this.makeSlot(skillArea, 'V', true);
    const rows = h('div', 'hud-side', skillArea);
    const top = h('div', 'hud-row', rows);
    const bottom = h('div', 'hud-row offset', rows);
    for (const k of SKILL_SLOTS) this.slots.set(k, this.makeSlot('QWER'.includes(k) ? top : bottom, k));

    // Identity crest.
    const crest = h('div', 'crest', this.cluster);
    crest.innerHTML = `<svg viewBox="0 0 100 100">
      <defs><radialGradient id="crestbg" cx="50%" cy="45%" r="60%"><stop offset="0%" stop-color="#3a2a1e"/><stop offset="100%" stop-color="#0c0908"/></radialGradient></defs>
      <polygon points="50,2 94,26 94,74 50,98 6,74 6,26" fill="url(#crestbg)" stroke="#836d22" stroke-width="2"/>
      <polygon points="50,9 88,30 88,70 50,91 12,70 12,30" fill="none" stroke="#3b3428" stroke-width="1.5"/>
      <circle cx="50" cy="50" r="30" fill="none" stroke="#1a1410" stroke-width="7"/>
      <circle class="ring" cx="50" cy="50" r="30" fill="none" stroke="#ff9a3c" stroke-width="6" stroke-linecap="round" transform="rotate(-90 50 50)" stroke-dasharray="0 999"/>
    </svg>`;
    this.crestRing = crest.querySelector('circle.ring') as SVGCircleElement;
    this.crestLabel = h('div', 'crest-label', crest);
    this.crestPips = h('div', 'pips', crest);
    this.zBtn = h('div', 'zx z', crest, 'Z');
    this.xBtn = h('div', 'zx x', crest, 'X');
    this.dashPip = h('div', 'dash-pip', crest, 'SPACE');

    const right = h('div', 'hud-side', this.cluster);
    this.buffsR = h('div', 'buff-row right', right);
    const mpBar = h('div', 'hud-bar mp right', right);
    this.mpFill = h('i', '', mpBar);
    this.mpText = h('span', '', mpBar);
    const itemRow = h('div', 'hud-row', right);
    for (let i = 0; i < 4; i++) this.itemSlots.push(this.makeSlot(itemRow, String(i + 1)));

    // XP + levels.
    const xp = h('div', 'xp-bar', r);
    this.xpFill = h('i', '', xp);
    const tabs = h('div', 'level-tabs', r);
    const t1 = h('span', '', tabs);
    this.lvlText = h('b', '', t1);
    const t2 = h('span', 'roster', tabs);
    this.rosterText = h('b', '', t2);

    this.toasts = h('div', 'toast-stack', r);
    this.promptEl = h('div', 'prompt hidden', r);
    this.menuBar = h('div', 'menu-bar', r);
  }

  /** Adds a menu-bar button; `key` may be a getter so labels follow rebinding (see `refreshMenuKeys`). */
  addMenuButton(label: string, key: string | (() => string), onClick: () => void): void {
    const b = h('button', '', this.menuBar);
    h('span', 'lbl', b, label);
    const k = h('span', 'k', b);
    const read = typeof key === 'string' ? () => key : key;
    k.textContent = read();
    this.menuKeys.push({ el: k, read });
    b.addEventListener('click', onClick);
  }

  /** Re-reads every live key label (after keybinds change). */
  refreshMenuKeys(): void {
    for (const m of this.menuKeys) m.el.textContent = m.read();
  }

  // ---------------------------------------------------------------- events

  zoneBanner(name: string, subtitle?: string): void {
    for (const old of this.root.querySelectorAll('.zone-banner')) old.remove();
    const b = h('div', 'zone-banner', this.root);
    h('h1', '', b, name);
    h('div', 'rule', b);
    if (subtitle) h('p', '', b, subtitle);
    // Gone when its fade ends. The fade starts on the first frame that draws it, which can come seconds
    // after a zone load on a slow device (compiling shaders); a fixed timer could expire unseen.
    b.addEventListener('animationend', () => b.remove());
    setTimeout(() => b.remove(), 10_000);
  }

  announce(text: string, style: 'boss' | 'mechanic' | 'info' | 'warning' | 'success' = 'info'): void {
    for (const old of this.root.querySelectorAll('.announce')) old.remove();
    const a = h('div', `announce ${style}`, this.root, text);
    setTimeout(() => a.remove(), 3300);
  }

  callout(text: string): void {
    for (const old of this.root.querySelectorAll('.big-callout')) old.remove();
    const a = h('div', 'big-callout', this.root, text);
    setTimeout(() => a.remove(), 1700);
  }

  perfect(text = 'Perfect!'): void {
    const a = h('div', 'perfect-pop', this.root, text);
    setTimeout(() => a.remove(), 950);
  }

  toast(html: string, color?: string): void {
    const t = h('div', 'toast', this.toasts);
    t.innerHTML = html;
    if (color) t.style.borderRightColor = color;
    setTimeout(() => t.remove(), 4100);
    while (this.toasts.children.length > 6) this.toasts.firstChild?.remove();
  }

  // ---------------------------------------------------------------- update

  update(dt: number, s: HudState, camera: THREE.Camera): void {
    this.time += dt;
    const p = s.player;
    const st = p.stats;
    // HP / MP.
    const hpPct = Math.max(0, p.hp / st.maxHp);
    if (Math.abs(hpPct - this.lastHp) > 0.0005) {
      this.hpFill.style.setProperty('--v', hpPct.toFixed(4));
      this.hpLag.style.setProperty('--v', hpPct.toFixed(4));
      this.lastHp = hpPct;
    }
    const sh = Math.min(1, p.shield / st.maxHp);
    if (Math.abs(sh - this.lastShield) > 0.001) {
      this.hpShield.style.setProperty('--v', sh.toFixed(4));
      this.lastShield = sh;
    }
    const hpStr = `${Math.ceil(p.hp).toLocaleString('en-US')} / ${Math.round(st.maxHp).toLocaleString('en-US')}${p.shield > 0 ? ` (+${Math.round(p.shield).toLocaleString('en-US')})` : ''}`;
    if (this.hpText.textContent !== hpStr) this.hpText.textContent = hpStr;
    const mpPct = st.maxMp > 0 ? p.mp / st.maxMp : 0;
    if (Math.abs(mpPct - this.lastMp) > 0.001) {
      this.mpFill.style.setProperty('--v', mpPct.toFixed(4));
      this.lastMp = mpPct;
    }
    const mpStr = `${Math.floor(p.mp)} / ${Math.round(st.maxMp)}`;
    if (this.mpText.textContent !== mpStr) this.mpText.textContent = mpStr;

    // Skill slots.
    const pl = p.player;
    if (pl) {
      const cls = s.cls;
      SKILL_SLOTS.forEach((k, i) => {
        const el = this.slots.get(k) as SlotEls;
        const base = pl.bar[i] ?? null;
        const id = cls.identity.skillForSlot ? cls.identity.skillForSlot(p, i, base) : base;
        this.updateSlot(el, id ? (p.skills.get(id) ?? null) : null, s.world, p, cls.hue);
      });
      this.updateSlot(this.awakeSlot, pl.awakening ? (p.skills.get(pl.awakening) ?? null) : null, s.world, p, cls.hue);
      const uses = pl.awakeningUses >= 99 ? '' : String(pl.awakeningUses);
      if (this.awakeSlot.count.textContent !== uses) this.awakeSlot.count.textContent = uses;

      // Crest.
      const id = pl.identity;
      const d = cls.identity.describe?.(p) ?? { label: cls.identityName, ready: id.gauge >= id.max, active: false };
      const frac = Math.max(0, Math.min(1, id.gauge / Math.max(1, id.max)));
      const circ = 2 * Math.PI * 30;
      this.crestRing.setAttribute('stroke-dasharray', `${(circ * frac).toFixed(1)} 999`);
      this.crestRing.setAttribute('stroke', d.active ? '#ff4a2a' : d.ready ? '#ffd24a' : `#${cls.hue.toString(16).padStart(6, '0')}`);
      if (this.crestLabel.textContent !== d.label) this.crestLabel.textContent = d.label;
      this.zBtn.classList.toggle('ready', d.ready);
      this.xBtn.classList.toggle('ready', d.active);
      const pipsKey = `${d.pips ?? 0}/${d.pipsMax ?? 0}`;
      if (this.crestPips.dataset.k !== pipsKey) {
        this.crestPips.dataset.k = pipsKey;
        this.crestPips.innerHTML = '';
        for (let k = 0; k < (d.pipsMax ?? 0); k++) h('div', `pip${k < (d.pips ?? 0) ? ' on' : ''}`, this.crestPips);
      }
      // Dash.
      const dashCd = p.cooldowns.get(s.world.skillForKey(p, 'SPACE') ?? pl.dash) ?? 0;
      const dashTxt = dashCd > 0 ? `SPACE ${dashCd.toFixed(1)}` : 'SPACE';
      if (this.dashPip.textContent !== dashTxt) this.dashPip.textContent = dashTxt;
      this.dashPip.classList.toggle('ready', dashCd <= 0);
    }

    // Items.
    s.items.forEach((it, i) => {
      const el = this.itemSlots[i];
      if (!el) return;
      if (!it) {
        el.root.classList.add('empty');
        if (el.img.src) el.img.removeAttribute('src');
        el.count.textContent = '';
        el.cd.style.setProperty('--cd', '0turn');
        el.cdn.textContent = '';
        return;
      }
      el.root.classList.remove('empty');
      if (el.img.getAttribute('src') !== it.icon) el.img.src = it.icon;
      el.root.title = it.name;
      const c = String(it.count);
      if (el.count.textContent !== c) el.count.textContent = c;
      const f = it.cdMax > 0 ? it.cd / it.cdMax : 0;
      el.cd.style.setProperty('--cd', `${f.toFixed(3)}turn`);
      el.cdn.textContent = it.cd > 0 ? Math.ceil(it.cd).toString() : '';
      el.root.classList.toggle('blocked', it.count <= 0);
    });

    this.updateBuffs(p);
    this.updateCastBar(p);

    // XP + level.
    this.xpFill.style.setProperty('--v', s.xpPct.toFixed(4));
    const lt = `Combat Lv.${s.level}`;
    if (this.lvlText.textContent !== lt) this.lvlText.textContent = lt;
    const rt = `Legacy Rank ${s.legacyRank}`;
    if (this.rosterText.textContent !== rt) this.rosterText.textContent = rt;
    if (this.zoneText.textContent !== s.zoneName) this.zoneText.textContent = s.zoneName;
    if (this.chText.textContent !== s.channel) this.chText.textContent = s.channel;

    // Info strip.
    const cur = `<span class="cur"><i class="coin silver"></i>${s.currencies.silver.toLocaleString('en-US')}</span><span class="cur"><i class="coin gold"></i>${s.currencies.gold.toLocaleString('en-US')}</span><span class="cur"><i class="coin shard"></i>${s.currencies.shards.toLocaleString('en-US')}</span>`;
    if (this.infoStrip.dataset.k !== cur) {
      this.infoStrip.dataset.k = cur;
      this.infoStrip.innerHTML = cur;
    }

    this.updateTracker(s.tracker);
    this.updateParty(s.party);
    this.updateBoss(s.world, p);

    if (s.prompt) {
      this.promptEl.classList.remove('hidden');
      const html = `<span class="keycap">${s.prompt.key}</span><span>${s.prompt.text}</span>`;
      if (this.promptEl.innerHTML !== html) this.promptEl.innerHTML = html;
    } else this.promptEl.classList.add('hidden');

    const w = this.root.clientWidth;
    const hh = this.root.clientHeight;
    this.minimap.draw(p.pos, s.minimapIcons);
    this.damage.update(dt, camera, w, hh);
    void this.tmp;
  }

  private updateSlot(el: SlotEls, skill: ResolvedSkill | null, world: World, p: Actor, hue: number): void {
    if (!skill) {
      if (el.skillId !== null) {
        el.skillId = null;
        el.root.classList.add('empty');
        el.img.removeAttribute('src');
      }
      el.cd.style.setProperty('--cd', '0turn');
      el.cdn.textContent = '';
      return;
    }
    if (el.skillId !== skill.id) {
      el.skillId = skill.id;
      el.root.classList.remove('empty');
      el.img.src = skillIcon(skill, hue);
      el.root.dataset.skill = skill.id;
    }
    const cd = p.cooldowns.get(skill.id) ?? 0;
    const total = skill.cooldown * (1 - p.stats.cdr);
    const frac = total > 0 ? Math.min(1, cd / total) : 0;
    if (Math.abs(frac - el.lastCd) > 0.002) {
      el.cd.style.setProperty('--cd', `${frac.toFixed(3)}turn`);
      el.lastCd = frac;
    }
    const charges = skill.charges && skill.charges > 1 ? chargesAvailable(p, skill) : 0;
    const txt = cd > 0 && (!skill.charges || charges === 0) ? (cd >= 10 ? Math.ceil(cd).toString() : cd.toFixed(1)) : '';
    if (el.cdn.textContent !== txt) el.cdn.textContent = txt;
    const cnt = skill.charges && skill.charges > 1 ? String(charges) : '';
    if (el.count.textContent !== cnt) el.count.textContent = cnt;
    const cooling = cd > 0;
    if (el.wasCooling && !cooling) {
      el.root.classList.remove('ready-flash');
      void el.root.offsetWidth;
      el.root.classList.add('ready-flash');
    }
    el.wasCooling = cooling;
    const block = canCast(world, p, skill);
    el.root.classList.toggle('nomana', block === 'mana');
    el.root.classList.toggle('blocked', block === 'silenced' || block === 'disabled' || block === 'identity' || block === 'resource' || block === 'uses');
    el.root.classList.toggle('active', p.cast?.skill.id === skill.id);
  }

  private updateBuffs(p: Actor): void {
    const visible = p.statuses.filter((s) => !getStatus(s.id).hidden);
    const key = visible.map((s) => `${s.id}:${Math.ceil(s.remaining)}:${s.stacks}`).join('|');
    if (key === this.buffKey) return;
    this.buffKey = key;
    this.buffsL.innerHTML = '';
    this.buffsR.innerHTML = '';
    for (const s of visible) {
      const def = getStatus(s.id);
      const el = h('div', `buff${def.kind === 'debuff' ? ' debuff' : ''}`, def.kind === 'debuff' ? this.buffsR : this.buffsL);
      el.title = `${def.name}: ${def.description}`;
      const img = h('img', '', el);
      img.src = statusIcon(s.id);
      if (s.remaining < 1e6) h('div', 't', el, s.remaining >= 60 ? `${Math.ceil(s.remaining / 60)}m` : `${Math.ceil(s.remaining)}s`);
      if (s.stacks > 1) h('div', 'st', el, `x${s.stacks}`);
    }
  }

  private updateCastBar(p: Actor): void {
    const c = p.cast;
    const show = !!c && (c.phase === 'casting' || c.phase === 'holding' || c.phase === 'charging');
    this.castbar.classList.toggle('hidden', !show);
    if (!show || !c) return;
    const s = c.skill;
    let v: number;
    let label = s.name;
    if (c.phase === 'casting') v = c.t / Math.max(0.01, c.duration);
    else if (c.phase === 'holding') v = c.holdTime / Math.max(0.01, s.holding?.maxHold ?? 1);
    else {
      const max = c.duration;
      v = c.holdTime / Math.max(0.01, max);
      label = `${s.name}${c.chargeLevel > 0 ? ` — Lv ${c.chargeLevel}` : ''}`;
    }
    this.castFill.style.setProperty('--v', Math.min(1, v).toFixed(3));
    const pz = c.phase === 'holding' ? s.holding?.perfectZone : c.phase === 'charging' ? s.charge?.overcharge : undefined;
    const max = c.phase === 'holding' ? (s.holding?.maxHold ?? 1) : c.duration;
    if (pz && max > 0) {
      this.castZone.classList.remove('hidden');
      this.castZone.style.left = `${((pz[0] / max) * 100).toFixed(1)}%`;
      this.castZone.style.width = `${(((pz[1] - pz[0]) / max) * 100).toFixed(1)}%`;
    } else this.castZone.classList.add('hidden');
    if (this.castText.textContent !== label) this.castText.textContent = label;
  }

  private updateTracker(entries: TrackerEntry[]): void {
    const key = JSON.stringify(entries);
    if (key === this.trackerKey) return;
    this.trackerKey = key;
    this.tracker.innerHTML = '';
    entries.slice(0, 7).forEach((e, i) => {
      const q = h('div', `q ${e.kind === 'weekly' ? 'daily' : e.kind}`, this.tracker);
      const t = h('div', 'qt', q);
      h('span', '', t, String(i + 1));
      h('i', 'dia', t);
      h('span', '', t, e.title);
      for (const o of e.objectives) {
        const line = h('div', `qo${o.done ? ' done' : ''}`, q);
        h('span', '', line, `• ${o.text}`);
        if (o.progress) h('span', '', line, o.progress);
      }
    });
  }

  private updateParty(members: PartyMemberView[]): void {
    const key = JSON.stringify(members.map((m) => [m.name, Math.round(m.hpPct * 100), m.dead, m.leader]));
    if (key === this.partyKey) return;
    this.partyKey = key;
    this.party.classList.toggle('hidden', members.length === 0);
    this.party.innerHTML = '';
    if (members.length === 0) return;
    h('div', 'ph', this.party, 'Party');
    for (const m of members) {
      const row = h('div', `pm${m.dead ? ' dead' : ''}`, this.party);
      h('div', `med${m.leader ? ' leader' : ''}`, row, m.classLetter);
      const col = h('div', '', row);
      const n = h('div', 'pn', col);
      h('span', '', n, m.name);
      h('small', '', n, `Lv.${m.level}`);
      const bar = h('div', 'php', col);
      const fill = h('i', '', bar);
      fill.style.setProperty('--v', m.hpPct.toFixed(3));
    }
  }

  private pickBoss(world: World, p: Actor): Actor | null {
    let best: Actor | null = null;
    let bestScore = Infinity;
    for (const a of world.aliveActors) {
      const m = a.monster;
      if (!m || GRADE_RANK[m.grade] < GRADE_RANK.named) continue;
      const d = Math.hypot(a.pos.x - p.pos.x, a.pos.z - p.pos.z);
      const engaged = m.targetId !== null || a.hp < a.stats.maxHp;
      if (d > (engaged ? 60 : 25)) continue;
      const score = d - (engaged ? 100 : 0);
      if (score < bestScore) {
        bestScore = score;
        best = a;
      }
    }
    return best;
  }

  private updateBoss(world: World, p: Actor): void {
    const b = this.pickBoss(world, p);
    this.boss.classList.toggle('hidden', !b);
    if (!b || !b.monster) {
      this.bossId = -1;
      return;
    }
    const m = b.monster;
    if (this.bossId !== b.id) {
      this.bossId = b.id;
      this.bossName.textContent = b.name;
      this.bossGrade.textContent = bossTitle(m.grade, m.tags, this.zoneKind);
      this.bossLvl.textContent = `Lv.${m.level}`;
    }
    const bars = Math.max(1, m.hpBars);
    const total = b.hpPct * bars;
    const remaining = Math.ceil(total - 1e-9);
    const within = remaining > 0 ? total - (remaining - 1) : 0;
    const col = LAYER_COLORS[(remaining - 1 + LAYER_COLORS.length * 10) % LAYER_COLORS.length] as string;
    const next = remaining > 1 ? (LAYER_COLORS[(remaining - 2 + LAYER_COLORS.length * 10) % LAYER_COLORS.length] as string) : '#120a0a';
    this.bossCur.style.background = `linear-gradient(${col}, ${shade(col)})`;
    this.bossNext.style.background = `linear-gradient(${next}, ${shade(next)})`;
    this.bossCur.style.setProperty('--v', within.toFixed(4));
    const num = `${Math.ceil(b.hp).toLocaleString('en-US')} / ${Math.round(b.stats.maxHp).toLocaleString('en-US')}`;
    if (this.bossNum.textContent !== num) this.bossNum.textContent = num;
    const mult = bars > 1 ? `x${remaining}` : '';
    if (this.bossMult.textContent !== mult) this.bossMult.textContent = mult;
    const stg = m.stagger;
    this.bossStagger.classList.toggle('hidden', !stg);
    if (stg) {
      this.bossStagger.classList.toggle('recover', !stg.active);
      const v = stg.active ? stg.value / stg.max : 1 - Math.max(0, stg.recoverTimer) / 12;
      this.bossStaggerFill.style.setProperty('--v', Math.max(0, Math.min(1, v)).toFixed(3));
    }
    const status = b.hasStatus('staggered') ? 'Staggered!' : m.countered > 0 ? 'Countered!' : m.counterUntil > 0 ? 'Counter window' : 'Immune to push · CC';
    const left = `${registryCreature(b)} · ${status}`;
    if (this.bossInfoL.textContent !== left) this.bossInfoL.textContent = left;
    const script = getBossScript(m.defId);
    const enrage = script && script.enrageAfter > 0 ? Math.max(0, script.enrageAfter - m.age) : -1;
    const er = enrage >= 0 ? `Enrage in ${fmtTime(enrage)}` : m.weakness ? `Weak to ${m.weakness}` : '';
    if (this.bossInfoR.textContent !== er) this.bossInfoR.textContent = er;
    this.bossEmblem.classList.toggle('enraged', b.hasStatus('enraged'));
    const partsHtml = m.parts.map((pt) => `<span class="part${pt.broken ? ' broken' : ''}">⌖ ${pt.name}</span>`).join('');
    if (this.bossParts.innerHTML !== partsHtml) this.bossParts.innerHTML = partsHtml;
  }

  labelWorld(infos: Parameters<WorldLabels['update']>[0], camera: THREE.Camera): void {
    this.labels.update(infos, camera, this.root.clientWidth, this.root.clientHeight);
  }
}

function registryCreature(a: Actor): string {
  return a.monster ? (a.monster.tags.includes('rift') ? 'Demon' : (a.look.variant ?? 'Monster')) : '';
}

function shade(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * 0.55);
  const g = Math.round(((n >> 8) & 255) * 0.55);
  const b = Math.round((n & 255) * 0.55);
  return `rgb(${r},${g},${b})`;
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Grade line above the boss bar, by content type (as Lost Ark labels guardians, legion commanders…). */
export function bossTitle(grade: string, tags: readonly string[], zoneKind: string): string {
  if (tags.includes('fieldBoss')) return 'Field Boss';
  if (grade === 'raid' || zoneKind === 'siege') return 'Warlord';
  if (zoneKind === 'colossus') return 'Colossus';
  if (zoneKind === 'deepvault') return 'Deepvault';
  if (zoneKind === 'rift') return 'Rift Lord';
  if (grade === 'boss') return 'Boss';
  return 'Named';
}

